import React, { useState } from 'react'
import { observer } from 'mobx-react-lite'
import { Download, BoxArrowUpRight, ArrowRepeat } from 'react-bootstrap-icons'
import axiosService from '../../../utils/AxiosService'
import { toastSuccess, toastError } from '../../FormComponents/Toasts'
import DraftStatusBadge from '../../Shared/DraftStatusBadge'
import { clientFeedbackSentOverride } from '../../../utils/draftStatus'

// The client's own read-only counterpart to SubmissionReview.jsx - nothing
// here is editable, and approved/revision-in-progress get their own
// simplified states below instead of the full form.
export default observer(function ClientReview({ action }) {
  const { action_draft: actionDraft } = action
  const version = actionDraft.latestVersion
  const [feedback, setFeedback] = useState('')
  const [busy, setBusy] = useState(false)

  const base = `/api/advertiser/draft_share/draft_versions/${version?.id}`

  const requestRevision = () => {
    if (!feedback.trim()) return toastError('Add feedback before requesting a revision')

    setBusy(true)
    axiosService.post(`${base}/client_request_revision`, { feedback })
      .then(() => {
        version.client_feedback_submitted = true
        toastSuccess('Feedback sent to Reachbird')
      })
      .catch(({ data: { error } }) => toastError(error))
      .finally(() => setBusy(false))
  }

  const approve = () => {
    setBusy(true)
    axiosService.post(`${base}/client_approve`)
      .then(() => {
        actionDraft.draft_status = 'approved'
        toastSuccess('Approved')
      })
      .catch(({ data: { error } }) => toastError(error))
      .finally(() => setBusy(false))
  }

  if (!version) return null

  const isApproved = actionDraft.draft_status === 'approved'
  const isRevisionInProgress = actionDraft.draft_status === 'revision_requested'
  const statusOverride = clientFeedbackSentOverride('client', actionDraft.draft_status, version.client_feedback_submitted)

  return (
    <div className="flex flex-col items-center">
      <div className="my-5 p-3 rounded-lg bg-white dark:bg-rb-dark-mode flex justify-between items-center w-[650px] border-1 border-rb-widget-border dark:border-rb-dark-mode-lighter">
        <h2 className="font-bold text-lg dark:text-white">{action.name}</h2>
        <DraftStatusBadge draftStatus={actionDraft.draft_status} role="client" {...statusOverride} />
      </div>

      {isRevisionInProgress && (
        <div className="border-1 border-orange-300 bg-orange-50 dark:bg-orange-900/20 rounded-lg p-8 w-[650px] mb-8 flex flex-col items-center text-center gap-2">
          <ArrowRepeat className="text-2xl text-orange-500" />
          <p className="font-bold text-orange-700 dark:text-orange-300">Revision requested</p>
          <p className="text-sm text-orange-700 dark:text-orange-400">
            Thanks for your feedback - the team is working on it. We'll let you know once it's ready for you to review again.
          </p>
        </div>
      )}

      {!isRevisionInProgress && actionDraft.fieldEnabled('upload') && version.draft_assets.length > 0 && (
        <div className="border-1 border-rb-widget-border dark:border-rb-dark-mode-lighter rounded-lg p-4 w-[650px] mb-4 bg-white dark:bg-rb-dark-mode">
          <p className="font-bold mb-2 dark:text-white">Content</p>
          <div className="flex flex-wrap gap-3 mb-3">
            {version.draft_assets.map(asset => (
              ['mp4', 'mov'].includes(asset.file_type) ? (
                <video key={asset.id} src={asset.url} controls className="max-h-60 rounded-lg" />
              ) : (
                <img key={asset.id} src={asset.url} className="max-h-60 rounded-lg" />
              )
            ))}
          </div>
          <div className="flex gap-2">
            <a href={version.draft_assets[0].url} download className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium rounded-lg border-1 border-rb-widget-border dark:border-rb-dark-mode-lighter dark:text-white">
              <Download /> Download
            </a>
            <a href={version.draft_assets[0].url} target="_blank" rel="noreferrer" className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium rounded-lg border-1 border-rb-widget-border dark:border-rb-dark-mode-lighter dark:text-white">
              <BoxArrowUpRight /> Open Asset
            </a>
          </div>
        </div>
      )}

      {!isRevisionInProgress && actionDraft.fieldEnabled('caption') && version.caption && (
        <div className="border-1 border-rb-widget-border dark:border-rb-dark-mode-lighter rounded-lg p-4 w-[650px] mb-4 bg-white dark:bg-rb-dark-mode">
          <p className="font-bold mb-1 dark:text-white">Caption</p>
          <p className="dark:text-white whitespace-pre-line">{version.caption}</p>
        </div>
      )}

      {!isRevisionInProgress && actionDraft.fieldEnabled('links_notes') && version.notes && (
        <div className="border-1 border-rb-widget-border dark:border-rb-dark-mode-lighter rounded-lg p-4 w-[650px] mb-4 bg-white dark:bg-rb-dark-mode">
          <p className="font-bold mb-1 dark:text-white">Links / Notes</p>
          <p className="dark:text-white whitespace-pre-line">{version.notes}</p>
        </div>
      )}

      {!isRevisionInProgress && (isApproved ? (
        <div className="border-1 border-rb-green bg-rb-soft-green dark:bg-rb-dark-mode-lighter rounded-lg p-4 w-[650px] mb-8">
          <p className="text-center font-bold text-rb-green">Thanks - this content is approved.</p>
        </div>
      ) : version.client_feedback_submitted ? (
        <div className="border-1 border-rb-widget-border dark:border-rb-dark-mode-lighter rounded-lg p-4 w-[650px] mb-8 bg-white dark:bg-rb-dark-mode">
          <p className="text-center dark:text-white">Thanks - your feedback has been sent to Reachbird.</p>
        </div>
      ) : (
        <>
          <div className="border-1 border-rb-widget-border dark:border-rb-dark-mode-lighter rounded-lg p-4 w-[650px] mb-4 bg-white dark:bg-rb-dark-mode">
            <p className="font-bold mb-2 dark:text-white">Your feedback</p>
            <textarea
              rows={3}
              value={feedback}
              onChange={e => setFeedback(e.target.value)}
              placeholder="Let us know what needs to change."
              className="w-full p-2 border-1 border-rb-lite-grey rounded-lg focus:ring-transparent focus:border-rb-green dark:bg-rb-dark-mode dark:text-white dark:border-rb-dark-mode-lighter"
            />
          </div>

          <div className="w-[650px] mb-8 flex justify-between gap-3">
            <button disabled={busy} onClick={requestRevision} className="flex-1 px-4 py-2 text-sm font-medium text-white bg-amber-500 rounded-lg disabled:opacity-50">
              Request Revision
            </button>
            <button disabled={busy} onClick={approve} className="flex-1 px-4 py-2 text-sm font-medium text-white bg-rb-green rounded-lg disabled:opacity-50">
              Approve
            </button>
          </div>
        </>
      ))}
    </div>
  )
})
